import React from 'react';

/**
 * Small pictures of what each design tier makes (14 Oct 2026, user: "clear by visual what is gonna come"):
 * Basic is a page of flat sections that also fits a phone; Extreme is the product as a 3D model taken apart, a camera
 * path around it, a copy card pinned to one part and a scroll rail. Static drawings: on hover the Extreme parts
 * spread a little further (a CSS transition, never a loop).
 */

const ACCENT = '#6c8cff';

/** x, y, width, height and an optional corner radius. */
type Bar = [number, number, number, number, number?];

const Bars = ({ bars, fill }: { bars: Bar[]; fill: string }) => (
  <>
    {bars.map(([x, y, width, height, radius = 2]) => (
      <rect key={`${x}:${y}`} x={x} y={y} width={width} height={height} rx={radius} fill={fill} />
    ))}
  </>
);

/** A browser window: the page ground, a title bar with its three dots and an address field. */
const BrowserWindow = ({ ground, bar, dot }: { ground: string; bar: string; dot: string }) => (
  <>
    <rect width="320" height="200" fill={ground} />
    <rect width="320" height="18" fill={bar} />
    {[12, 22, 32].map((cx) => <circle key={cx} cx={cx} cy="9" r="3" fill={dot} />)}
    <rect x="60" y="5" width="200" height="8" rx="4" fill={dot} opacity="0.5" />
  </>
);

const BasicHero = () => (
  <>
    <Bars bars={[[16, 28, 28, 8]]} fill="#4a4f5b" />
    <Bars bars={[[180, 30, 20, 4], [206, 30, 20, 4], [232, 30, 20, 4]]} fill="#3a3e48" />
    <rect x="268" y="26" width="36" height="11" rx="5.5" fill={ACCENT} />
    <Bars bars={[[16, 54, 128, 10], [16, 68, 96, 10]]} fill="#e6e8ee" />
    <Bars bars={[[16, 86, 120, 4], [16, 94, 96, 4]]} fill="#4a4f5b" />
    <rect x="16" y="106" width="48" height="12" rx="6" fill={ACCENT} />
    <rect x="70.5" y="106.5" width="44" height="11" rx="5.5" fill="none" stroke="#4a4f5b" />
    <rect x="168" y="48" width="136" height="72" rx="6" fill="#2c2f37" />
    <path d="M176 112 L204 84 L222 100 L236 88 L296 112 Z" fill="#3a3e48" />
    <circle cx="280" cy="66" r="7" fill="#4a4f5b" />
  </>
);

const BasicSections = () => (
  <>
    {[16, 116, 216].map((x) => (
      <g key={x}>
        <rect x={x} y="134" width="88" height="40" rx="6" fill="#262930" stroke="#30343d" />
        <circle cx={x + 13} cy="147" r="5" fill={ACCENT} opacity="0.55" />
        <rect x={x + 10} y="158" width="60" height="4" rx="2" fill="#4a4f5b" />
        <rect x={x + 10} y="165" width="42" height="4" rx="2" fill="#3a3e48" />
      </g>
    ))}
    <rect y="182" width="320" height="18" fill="#24272e" />
    <Bars bars={[[16, 189, 40, 4], [250, 189, 54, 4]]} fill="#3a3e48" />
  </>
);

/** The same page on a phone: one column. */
const BasicPhone = () => (
  <g className="dtv-phone">
    <rect x="258" y="92" width="50" height="98" rx="8" fill="#17191e" stroke="#4a4f5b" strokeWidth="1.2" />
    <Bars bars={[[276, 96, 14, 3, 1.5], [264, 104, 38, 5, 1], [264, 112, 38, 24, 3], [264, 170, 38, 14, 3]]}
      fill="#2c2f37" />
    <Bars bars={[[264, 141, 34, 5]]} fill="#e6e8ee" />
    <Bars bars={[[264, 150, 28, 3]]} fill="#4a4f5b" />
    <rect x="264" y="158" width="22" height="7" rx="3.5" fill={ACCENT} />
  </g>
);

export const BasicDesignVisual = () => (
  <svg viewBox="0 0 320 200" role="presentation" focusable="false">
    <BrowserWindow ground="#1d1f25" bar="#24272e" dot="#3a3e48" />
    <BasicHero />
    <BasicSections />
    <BasicPhone />
  </svg>
);

interface Slab { cx: number; cy: number; w: number; h: number; t: number }

/** A flat box seen from above at an angle: its top face and the two side faces below it. */
interface Faces { top: string; left: string; right: string; edge: string }

const IsoSlab = ({ slab, top, left, right, edge }: Faces & { slab: Slab }) => {
  const { cx, cy, w, h, t } = slab;

  return (
    <g stroke={edge} strokeWidth="0.8" strokeLinejoin="round">
      <polygon points={`${cx - w},${cy} ${cx},${cy + h} ${cx},${cy + h + t} ${cx - w},${cy + t}`} fill={left} />
      <polygon points={`${cx},${cy + h} ${cx + w},${cy} ${cx + w},${cy + t} ${cx},${cy + h + t}`} fill={right} />
      <polygon points={`${cx},${cy - h} ${cx + w},${cy} ${cx},${cy + h} ${cx - w},${cy}`} fill={top} />
    </g>
  );
};

const rhombus = (cx: number, cy: number, w: number) => (
  `${cx},${cy - w / 2} ${cx + w},${cy} ${cx},${cy + w / 2} ${cx - w},${cy}`
);

/** The circuit board's chips and traces (the part the copy card is about). */
const BoardDetails = () => (
  <g>
    <path d="M178 84 l12 6 l14 -7 M206 77 l16 8 M184 91 l10 -5" fill="none" stroke="#49d3c4" strokeWidth="0.8"
      opacity="0.7" />
    {[[180, 82, 9], [204, 74, 7], [218, 88, 8], [198, 94, 6]].map(([x, y, w]) => (
      <polygon key={`${x}:${y}`} points={rhombus(x, y, w)} fill="#0b1416" stroke="#49d3c4" strokeWidth="0.6" />
    ))}
  </g>
);

interface Layer extends Faces { key: string; slab: Slab; details?: React.ReactNode }

/** Bottom to top: each part is drawn over the one below it. */
const LAYERS: Layer[] = [
  {
    key: 'base', slab: { cx: 200, cy: 138, w: 52, h: 26, t: 12 }, top: '#353a46', left: '#20242c', right: '#2a2e38',
    edge: 'rgba(255,255,255,0.14)', details: <polygon points={rhombus(200, 138, 40)} fill="#1d2027" />,
  },
  {
    key: 'cell', slab: { cx: 200, cy: 111, w: 42, h: 21, t: 10 }, top: 'url(#dtv-cell)', left: '#7a3f22',
    right: '#9a5130', edge: 'rgba(255,170,120,0.45)',
    details: <polygon points={rhombus(200, 111, 16)} fill="rgba(255,255,255,0.18)" />,
  },
  {
    key: 'board', slab: { cx: 200, cy: 84, w: 52, h: 26, t: 4 }, top: 'url(#dtv-board)', left: '#123b38',
    right: '#18504b', edge: 'rgba(73,211,196,0.55)', details: <BoardDetails />,
  },
  {
    key: 'cover', slab: { cx: 200, cy: 56, w: 52, h: 26, t: 3 }, top: 'rgba(170,190,255,0.12)',
    left: 'rgba(170,190,255,0.08)', right: 'rgba(170,190,255,0.14)', edge: 'rgba(205,218,255,0.55)',
    details: <path d="M162 56 L196 39" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" strokeLinecap="round" />,
  },
];

const ExtremeDefs = () => (
  <defs>
    <radialGradient id="dtv-night" cx="0.62" cy="0.52" r="0.75">
      <stop offset="0" stopColor="#1a2242" />
      <stop offset="0.55" stopColor="#0d1018" />
      <stop offset="1" stopColor="#07080b" />
    </radialGradient>
    <radialGradient id="dtv-floor">
      <stop offset="0" stopColor={ACCENT} stopOpacity="0.35" />
      <stop offset="1" stopColor={ACCENT} stopOpacity="0" />
    </radialGradient>
    <linearGradient id="dtv-board" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor="#2a8f86" />
      <stop offset="1" stopColor="#1c5d58" />
    </linearGradient>
    <linearGradient id="dtv-cell" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor="#e0905e" />
      <stop offset="1" stopColor="#b5643a" />
    </linearGradient>
  </defs>
);

/** The camera's path around the product, with the camera on it looking in. */
const CameraPath = () => (
  <g>
    <ellipse cx="200" cy="104" rx="84" ry="28" fill="none" stroke={ACCENT} strokeOpacity="0.4" strokeDasharray="3 4" />
    <polygon points="280.5,99 287.5,99 284,105.5" fill={ACCENT} />
    <path d="M266 118 L250 110 M266 118 L250 126" stroke={ACCENT} strokeOpacity="0.3" />
    <rect x="266" y="113" width="13" height="9" rx="2" fill="#0d0f14" stroke={ACCENT} />
    <circle cx="272.5" cy="117.5" r="2.4" fill="none" stroke={ACCENT} />
    <rect x="268" y="110.5" width="4" height="2.5" rx="0.6" fill={ACCENT} />
  </g>
);

/** Dashed assembly lines: the parts come apart along one axis. */
const AssemblyLines = () => (
  <g stroke="rgba(255,255,255,0.14)" strokeDasharray="2 3">
    <path d="M200 30 V168 M148 56 V138 M252 56 V138" />
  </g>
);

/** The copy card of the active shot, its leader line ending on the part it is about. */
const ShotCard = () => (
  <g>
    <rect x="14.5" y="60.5" width="98" height="84" rx="6" fill="#11141a" stroke="#262b36" />
    <rect x="14" y="66" width="2" height="72" fill={ACCENT} />
    <Bars bars={[[24, 70, 34, 4]]} fill={ACCENT} />
    <Bars bars={[[24, 80, 70, 7], [24, 91, 52, 7]]} fill="#e9ecf3" />
    <Bars bars={[[24, 104, 74, 3.5], [24, 111, 64, 3.5], [24, 126, 26, 3.5], [24, 134, 22, 3.5]]} fill="#5b6272" />
    <Bars bars={[[80, 126, 18, 3.5], [76, 134, 22, 3.5]]} fill="#c9cfdb" />
    <path d="M24 122 H100" stroke="#2a303c" />
    <path d="M112 96 H138 L149 88" fill="none" stroke={ACCENT} />
    <circle cx="150" cy="87" r="2.4" fill="#11141a" stroke={ACCENT} />
  </g>
);

/** Where the visitor is in the scroll: one stop per camera shot. */
const ScrollRail = () => (
  <g>
    <path d="M304 36 V164" stroke="#232833" strokeWidth="1.5" />
    <path d="M304 36 V82" stroke={ACCENT} strokeWidth="1.5" />
    {[46, 118, 154].map((cy) => <circle key={cy} cx="304" cy={cy} r="2" fill="#3a4050" />)}
    <circle cx="304" cy="82" r="6" fill="none" stroke={ACCENT} strokeOpacity="0.35" />
    <circle cx="304" cy="82" r="3.4" fill={ACCENT} />
  </g>
);

export const ExtremeDesignVisual = () => (
  <svg viewBox="0 0 320 200" role="presentation" focusable="false">
    <ExtremeDefs />
    <BrowserWindow ground="url(#dtv-night)" bar="#0f1116" dot="#262a33" />
    <ellipse cx="200" cy="176" rx="70" ry="9" fill="url(#dtv-floor)" />
    <CameraPath />
    <AssemblyLines />
    {LAYERS.map(({ key, details, ...layer }) => (
      <g key={key} className={`dtv-layer dtv-layer--${key}`}>
        <IsoSlab {...layer} />
        {details}
      </g>
    ))}
    <ShotCard />
    <ScrollRail />
  </svg>
);
